import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

import { BrandingProvider } from "@/context/BrandingContext";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import DiscountPopup from "@/components/DiscountPopup";
import TrackingScripts from "@/components/TrackingScripts";
import ScrollToTop from "@/components/ScrollToTop";
import JsonLd, { buildOrganizationSchema, buildWebSiteSchema } from "@/components/JsonLd";
import { useBranding } from "@/context/BrandingContext";
import { api } from "@/lib/api";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Account from "@/pages/Account";
import Wishlist from "@/pages/Wishlist";
import { Blog, BlogDetail } from "@/pages/Blog";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import PageView from "@/pages/PageView";
import ThemeCollection from "@/pages/ThemeCollection";
import TrackOrder from "@/pages/TrackOrder";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminBlogs from "@/pages/admin/AdminBlogs";
import AdminSeo from "@/pages/admin/AdminSeo";
import AdminBranding from "@/pages/admin/AdminBranding";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminStories from "@/pages/admin/AdminStories";
import AdminRewards from "@/pages/admin/AdminRewards";
import AdminGate from "@/pages/admin/AdminGate";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminLowStock from "@/pages/admin/AdminLowStock";
import AdminBulkUpload from "@/pages/admin/AdminBulkUpload";
import AdminReports from "@/pages/admin/AdminReports";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminFeeds from "@/pages/admin/AdminFeeds";
import AdminPages from "@/pages/admin/AdminPages";
import AdminMarketing from "@/pages/admin/AdminMarketing";
import AdminTracking from "@/pages/admin/AdminTracking";
import AdminCoins from "@/pages/admin/AdminCoins";
import AdminShipping from "@/pages/admin/AdminShipping";
import AdminEmail from "@/pages/admin/AdminEmail";
import AdminSecurity from "@/pages/admin/AdminSecurity";
import AdminPayments from "@/pages/admin/AdminPayments";

const StoreShell = ({ children }) => {
  const location = useLocation();
  const { branding } = useBranding();
  const isAdmin = location.pathname.startsWith("/admin");
  const [popup, setPopup] = React.useState(null);
  const [tracking, setTracking] = React.useState(null);
  React.useEffect(() => {
    if (isAdmin) return;
    api.get("/marketing/config").then(r => setPopup(r.data?.popup || null)).catch(() => {});
    api.get("/tracking-codes").then(r => setTracking(r.data || null)).catch(() => {});
  }, [isAdmin]);
  if (isAdmin) return children;
  return (
    <>
      {/* Sitewide structured data: tells Google who you are + enables sitelinks search */}
      <JsonLd data={[buildOrganizationSchema(branding), buildWebSiteSchema(branding)]} />
      {tracking && <TrackingScripts codes={tracking} />}
      <Header />
      {children}
      <CartDrawer />
      {popup && <DiscountPopup config={popup} />}
      <Footer />
    </>
  );
};

function App() {
  return (
    <div className="App" data-testid="app-root">
      <HelmetProvider>
        <BrowserRouter>
          <ScrollToTop />
          <BrandingProvider>
            <AuthProvider>
              <CartProvider>
                <StoreShell>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/shop/characters/:themeSlug" element={<ThemeCollection />} />
                  <Route path="/shop/:category" element={<Shop />} />
                  <Route path="/product/:slug" element={<ProductDetail />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/checkout/success" element={<CheckoutSuccess />} />
                  <Route path="/account" element={<Account />} />
                  <Route path="/wishlist" element={<Wishlist />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogDetail />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/terms" element={<PageView />} />
                  <Route path="/privacy" element={<PageView />} />
                  <Route path="/returns" element={<PageView />} />
                  <Route path="/track" element={<TrackOrder />} />
                  <Route path="/track/:orderId" element={<TrackOrder />} />

                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/atelier-entry/:slug" element={<AdminGate />} />
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<Dashboard />} />
                    <Route path="analytics" element={<AdminAnalytics />} />
                    <Route path="low-stock" element={<AdminLowStock />} />
                    <Route path="bulk-upload" element={<AdminBulkUpload />} />
                    <Route path="reports" element={<AdminReports />} />
                    <Route path="audit" element={<AdminAudit />} />
                    <Route path="feeds" element={<AdminFeeds />} />
                    <Route index element={<Dashboard />} />
                    <Route path="products" element={<AdminProducts />} />
                    <Route path="categories" element={<AdminCategories />} />
                    <Route path="orders" element={<AdminOrders />} />
                    <Route path="customers" element={<AdminCustomers />} />
                    <Route path="blogs" element={<AdminBlogs />} />
                    <Route path="seo" element={<AdminSeo />} />
                    <Route path="branding" element={<AdminBranding />} />
                    <Route path="coupons" element={<AdminCoupons />} />
                    <Route path="reviews" element={<AdminReviews />} />
                    <Route path="stories" element={<AdminStories />} />
                    <Route path="rewards" element={<AdminRewards />} />
                    <Route path="coins" element={<AdminCoins />} />
                    <Route path="marketing" element={<AdminMarketing />} />
                    <Route path="tracking" element={<AdminTracking />} />
                    <Route path="shipping" element={<AdminShipping />} />
                    <Route path="email" element={<AdminEmail />} />
                    <Route path="security" element={<AdminSecurity />} />
                    <Route path="payments" element={<AdminPayments />} />
                    <Route path="pages" element={<AdminPages />} />
                  </Route>
                </Routes>
              </StoreShell>
            </CartProvider>
          </AuthProvider>
        </BrandingProvider>
      </BrowserRouter>
      </HelmetProvider>
      <Toaster position="bottom-center" richColors closeButton theme="light" />
    </div>
  );
}

export default App;
